import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Mail, Lock, Eye, EyeOff, AlertCircle, Shield, ArrowRight } from 'lucide-react';
import LifeShareLogo from '../components/LifeShareLogo';
import Toggle from '../components/Toggle';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [focused, setFocused] = useState<string | null>(null);

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const success = await login(email, password, rememberMe);
      if (success) {
        navigate('/');
      } else {
        setError('Email veya şifre hatalı');
      }
    } catch (err) {
      setError('Giriş yapılırken bir hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex relative overflow-hidden">
      {/* Left - Branding Side */}
      <div className="hidden lg:flex lg:w-1/2 gradient-lifeshare relative items-center justify-center p-12">
        {/* Decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-24 -left-24 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
          <div className="absolute -bottom-32 -right-32 w-[500px] h-[500px] bg-white/5 rounded-full blur-3xl" />
          <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-white/3 rounded-full blur-2xl" />
        </div>

        <div className="relative z-10 max-w-lg text-center">
          <div className="mb-8 flex justify-center">
            <div className="p-4 bg-white/10 backdrop-blur-md rounded-3xl border border-white/10">
              <LifeShareLogo
                size="large"
                variant="default"
                showText={false}
                className="mx-auto"
              />
            </div>
          </div>
          <h1 className="text-5xl font-bold text-white mb-4 tracking-tight">LifeShare</h1>
          <p className="text-xl text-white/80 font-medium mb-2">Admin Panel</p>
          <p className="text-white/50 text-sm max-w-sm mx-auto leading-relaxed">
            Ailelerinizi yönetin, konumları takip edin ve tüm kullanıcılarınızı güvenle kontrol edin.
          </p>

          {/* Feature badges */}
          <div className="flex flex-wrap justify-center gap-2 mt-8">
            {['Gerçek Zamanlı', 'Güvenli', 'Kolay Yönetim'].map((f) => (
              <span key={f} className="px-3 py-1.5 bg-white/10 backdrop-blur-sm border border-white/10 rounded-full text-xs text-white/80 font-medium">
                {f}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Right - Login Form Side */}
      <div className="flex-1 flex items-center justify-center p-6 bg-gray-50 lg:bg-white">
        <div className="w-full max-w-[420px]">

          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="inline-flex p-3 bg-purple-100 rounded-2xl mb-4">
              <LifeShareLogo
                size="large"
                variant="default"
                showText={false}
                className="mx-auto"
              />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-1">LifeShare</h1>
            <p className="text-gray-500 text-sm">Admin Panel Girişi</p>
          </div>

          {/* Welcome Text (Desktop) */}
          <div className="hidden lg:block mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-1">Hoş Geldiniz 👋</h2>
            <p className="text-gray-500">Yönetim paneline erişmek için giriş yapın</p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-100 rounded-2xl p-4 flex items-center gap-3 animate-slide-in">
                <div className="p-1.5 bg-red-100 rounded-xl">
                  <AlertCircle className="w-4 h-4 text-red-500" />
                </div>
                <p className="text-red-700 text-sm font-medium">{error}</p>
              </div>
            )}

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Adresi
              </label>
              <div className={`relative rounded-2xl transition-all duration-200 ${focused === 'email' ? 'ring-2 ring-purple-500 ring-offset-1' : ''}`}>
                <Mail className={`w-5 h-5 absolute left-4 top-1/2 transform -translate-y-1/2 transition-colors ${focused === 'email' ? 'text-purple-500' : 'text-gray-400'}`} />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onFocus={() => setFocused('email')}
                  onBlur={() => setFocused(null)}
                  className="w-full pl-12 pr-4 py-3.5 bg-gray-50 lg:bg-gray-50 rounded-2xl text-sm text-gray-900 placeholder-gray-400 outline-none border border-gray-200 focus:border-purple-300 focus:bg-white transition-colors"
                  placeholder="kullaniciadi@lifeshare.app"
                  required
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Şifre
              </label>
              <div className={`relative rounded-2xl transition-all duration-200 ${focused === 'password' ? 'ring-2 ring-purple-500 ring-offset-1' : ''}`}>
                <Lock className={`w-5 h-5 absolute left-4 top-1/2 transform -translate-y-1/2 transition-colors ${focused === 'password' ? 'text-purple-500' : 'text-gray-400'}`} />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => setFocused('password')}
                  onBlur={() => setFocused(null)}
                  className="w-full pl-12 pr-12 py-3.5 bg-gray-50 lg:bg-gray-50 rounded-2xl text-sm text-gray-900 placeholder-gray-400 outline-none border border-gray-200 focus:border-purple-300 focus:bg-white transition-colors"
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Remember Me Toggle */}
            <div className="flex items-center justify-between">
              <Toggle
                enabled={rememberMe}
                onChange={setRememberMe}
                label="Beni hatırla"
              />
            </div>

            {/* Login Button */}
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full py-3.5 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-semibold rounded-2xl transition-all duration-200 flex items-center justify-center gap-2 shadow-lg shadow-purple-500/25 ${isLoading ? 'opacity-70 cursor-not-allowed' : 'hover:shadow-xl hover:shadow-purple-500/30 active:scale-[0.98]'}`}
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>Giriş yapılıyor...</span>
                </div>
              ) : (
                <>
                  <span>Giriş Yap</span>
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>

          {/* Security Badge */}
          <div className="mt-8 flex items-center justify-center gap-2 text-xs text-gray-400">
            <Shield className="w-3.5 h-3.5" />
            <span>SSL ile korunan güvenli bağlantı</span>
          </div>

          {/* Footer */}
          <div className="mt-4 text-center">
            <p className="text-gray-400 text-xs">
              LifeShare Admin Panel v26.29
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
